// src/features/executive/performance/drill/RevenueDrill.tsx
// Drill body for the Revenue Trend widget. Reuses useRevenueTrend
// (cache hit with the parent card's filters) and renders a chart
// header + percent-of-total table.
import { Box, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Bar } from 'react-chartjs-2'
import { useRevenueTrend } from '../hooks/use-revenue-trend'
import DrillTable from 'features/executive/components/DrillTable/DrillTable'
import type { ExecutiveState } from 'app/slice/executive.slice'
import type { RevenueTrendPoint } from 'api/executive/executive.types'
import { formatCurrency, formatDateThaiShort } from 'utils/executive.utils'
import { useRegisterDrillExport } from 'features/executive/components/DrillDrawer/DrillExportContext'

export default function RevenueDrill() {
  const { t } = useTranslation()
  const stack = useSelector((s: { executive: ExecutiveState }) => s.executive.drillStack)
  const top = stack[stack.length - 1]
  const DRILL_WIDGET_KEY = 'performance.revenue-trend-drill'
  const { data, loading, error, refetch } = useRevenueTrend(top?.scopedFilters, DRILL_WIDGET_KEY)

  const widgetTitle = top?.label ?? t('EXECUTIVE.PERFORMANCE.REVENUE.TITLE')
  useRegisterDrillExport(
    data ? { drillId: 'revenue', data } : null,
    DRILL_WIDGET_KEY,
    widgetTitle
  )

  const points: RevenueTrendPoint[] = data?.points ?? []
  const total = data?.totalActual ?? points.reduce((s, p) => s + p.actual, 0)

  const chartData = {
    labels: points.map((p) => formatDateThaiShort(p.bucket)),
    datasets: [{
      label: t('EXECUTIVE.PERFORMANCE.REVENUE.TITLE'),
      data: points.map((p) => p.actual),
      backgroundColor: '#2a8d9c',
    }],
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {top?.label ?? t('EXECUTIVE.PERFORMANCE.REVENUE.TITLE')}
      </Typography>
      <Box sx={{ height: 280, mb: 3 }}>
        <Bar
          data={chartData as any}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: { legend: { display: false } },
            scales: { y: { beginAtZero: true } },
          }}
        />
      </Box>
      <DrillTable
        rows={points}
        columns={[
          { key: 'bucket', label: t('EXECUTIVE.DRILL.DATE', 'Date'), render: (p) => formatDateThaiShort(p.bucket) },
          { key: 'amount', label: t('EXECUTIVE.DRILL.REVENUE', 'Revenue'), align: 'right', render: (p) => formatCurrency(p.actual) },
          {
            key: 'share',
            label: t('EXECUTIVE.DRILL.SHARE', '% of total'),
            align: 'right',
            render: (p) => (total > 0 ? `${((p.actual / total) * 100).toFixed(1)}%` : '—'),
          },
        ]}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    </Box>
  )
}
